import { socket } from '#GameFrontend/services/socket';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export interface BankBalances {
	cando: number;
	horseshoe: number;
	accountNumber: string | null;
}

interface BankContextType {
	balances: BankBalances;
	isLoading: boolean;
	refresh: () => void;
}

const defaultBalances: BankBalances = {
	cando: 0,
	horseshoe: 0,
	accountNumber: null,
};

const BankContext = createContext<BankContextType>({
	balances: defaultBalances,
	isLoading: true,
	refresh: () => {},
});

export const useBank = () => useContext(BankContext);

export const BankProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [balances, setBalances] = useState<BankBalances>(defaultBalances);
	const [isLoading, setIsLoading] = useState(true);

	const refresh = useCallback(() => {
		socket.timeout(5000).emit('Bank:Get', {});
	}, []);

	useEffect(() => {
		const onBankGet = (response: {
			success: boolean;
			data?: { cando?: number; horseshoe?: number; accountNumber?: string; balance?: number };
		}) => {
			setIsLoading(false);
			if (response.success && response.data) {
				setBalances({
					cando: response.data.cando ?? response.data.balance ?? 0,
					horseshoe: response.data.horseshoe ?? 0,
					accountNumber: response.data.accountNumber ?? null,
				});
			}
		};

		socket.on('Bank:Get', onBankGet);
		refresh();

		return () => {
			socket.off('Bank:Get', onBankGet);
		};
	}, [refresh]);

	return <BankContext.Provider value={{ balances, isLoading, refresh }}>{children}</BankContext.Provider>;
};
